import CategoryPills from "@/components/CategoryPills";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Briefcase, FolderOpen, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { PortfolioItem, getPublicPortfolioItems } from "@/lib/api";
import { slugify } from "@/lib/slug";
import { getPortfolioDescription, getPortfolioTitle } from "@/lib/portfolioTranslation";

const iconMap = [
  <Image className="h-8 w-8 text-brand-yellow" />,
  <FolderOpen className="h-8 w-8 text-brand-yellow" />,
  <Briefcase className="h-8 w-8 text-brand-yellow" />
];

const PortfolioPreview = () => {
  const { t, i18n } = useTranslation();
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPortfolioItems = async () => {
      try {
        const data = await getPublicPortfolioItems();
        const activeItems = data.slice(0, 3);
        setPortfolioItems(activeItems);
      } catch (error) {
        console.error("Error fetching portfolio items:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPortfolioItems();
  }, []);


  return (
    <section className="py-16 bg-white">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('portfolio.ourWorkTitle')}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('portfolio.ourWorkSubtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {isLoading ? (
            [1, 2, 3].map(i => (
              <Card key={i} className="overflow-hidden transition-all hover:shadow-lg">
                <div className="h-48 bg-muted animate-pulse"></div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-8 w-8 bg-muted animate-pulse rounded-full"></div>
                    <div className="h-6 bg-muted animate-pulse rounded w-2/3"></div>
                  </div>
                  <div className="h-12 bg-muted animate-pulse rounded mb-4"></div>
                  <div className="flex gap-2">
                    <div className="h-5 w-16 bg-muted animate-pulse rounded-full"></div>
                    <div className="h-5 w-20 bg-muted animate-pulse rounded-full"></div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            portfolioItems.map((project) => {
              const translatedTitle = getPortfolioTitle(project, i18n.language);
              const translatedDescription = getPortfolioDescription(project, i18n.language);

              return (
              <Link key={project.id} to={`/project/${slugify(project.title) || project.id}`} className="block h-full">
                <Card className="overflow-hidden transition-all hover:shadow-lg h-full">
                  <div className="h-48 bg-muted flex items-center justify-center overflow-hidden">
                    <img 
                      src={project.image_url} 
                      alt={translatedTitle}
                      className="h-full w-full object-cover" 
                      loading="lazy"
                      width="400"
                      height="192"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      {project.logo_url && (
                        <img 
                          src={project.logo_url} 
                          alt={`${translatedTitle} logo`}
                          className="h-12 w-auto max-w-[120px] object-contain"
                          loading="lazy"
                        />
                      )}
                      <h3 className="text-xl font-semibold">{translatedTitle}</h3>
                    </div>
                    <p className="text-muted-foreground mb-4">{translatedDescription}</p>
                    <CategoryPills slugs={project.categories} />
                  </CardContent>
                </Card>
              </Link>
              );
            })
          )}
        </div>
        
        <div className="mt-10 text-center">
          <Button asChild size="lg" className="bg-brand-black text-white hover:bg-brand-black/90">
            <Link to="/portfolio" className="flex items-center gap-2">
              {t('portfolio.viewAllProjects')} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PortfolioPreview;
